import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ResultsPaginationContainer from '@Containers/ResultsPagination';

import IntegratedFeedbackSlot from './IntegratedFeedbackSlot';
import ResultsListWithLoader from './ResultsListWithLoader';

/**
 * The inner content area of the results page: integrated feedback (top/bottom),
 * results list with loading spinner, and pagination.
 *
 * NOTE: the Footer is intentionally NOT rendered here. The shared Page shell renders it below
 * `.react-page--children-wrapper`, outside the two-column Results content.
 *
 * Pagination visibility logic lives here because it is only consumed by this
 * component. The pagination stays mounted during loading to prevent the GoToPage
 * button from unmounting with focus (which would drop focus to document.body).
 */
export default function ResultsContentSection({
  metadata,
  context,
  results,
  gridView,
  searchType,
  originalSearchType,
  currentPageSize,
  currentPage,
  totalPages,
  resultsLoading,
  paginationVisible,
  activeBreadcrumb,
}) {
  const paginationWasVisibleRef = useRef(false);
  if (paginationVisible) {
    paginationWasVisibleRef.current = true;
  } else if (!resultsLoading) {
    paginationWasVisibleRef.current = false;
  }

  // Keep pagination mounted during loading to prevent focus from dropping
  // to document.body when the GoToPage button unmounts with focus.
  const showPagination = paginationVisible || (resultsLoading && paginationWasVisibleRef.current);

  return (
    // FR-4.3: this is the inner content area of the results column (integrated feedback, results
    // list, pagination). The page's single `#main-content` landmark now lives on the `<main>` that
    // wraps the whole content area in `ResultsPage.jsx` (header→footer), so this element is a plain
    // presentational `<div>` — it no longer owns `id="main-content"` or a focus `tabIndex`.
    <div className="results-content">
      <IntegratedFeedbackSlot placement="top" searchType={searchType} activeBreadcrumb={activeBreadcrumb} />

      <ResultsListWithLoader
        metadata={metadata}
        context={context}
        results={results}
        gridView={gridView}
        searchType={searchType}
        originalSearchType={originalSearchType}
        size={currentPageSize}
        currentPage={currentPage}
        totalPages={totalPages}
        resultsLoading={resultsLoading}
      />

      <IntegratedFeedbackSlot placement="bottom" searchType={searchType} activeBreadcrumb={activeBreadcrumb} />

      {showPagination && <ResultsPaginationContainer />}
    </div>
  );
}

ResultsContentSection.propTypes = {
  metadata: MetadataPropTypesShape,
  context: PropTypes.string.isRequired,
  results: PropTypes.array.isRequired,
  gridView: PropTypes.bool,
  searchType: PropTypes.string,
  originalSearchType: PropTypes.string,
  currentPageSize: PropTypes.number,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  resultsLoading: PropTypes.bool,
  paginationVisible: PropTypes.bool,
  activeBreadcrumb: PropTypes.object,
};
